import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { api, API, fmtInt, getUser } from "@/lib/api";
import { useCanAdmin } from "@/lib/useCanAdmin";
import { useRealtimeOrders } from "@/lib/useRealtimeOrders";
import { Television, Clock, Warning, Tag, Storefront, Image as ImageIcon, ShareNetwork, Copy, ArrowsClockwise, X, Check } from "@phosphor-icons/react";
import OrderThumbs from "@/components/orders/OrderThumbs";

function ShareTvModal({ onClose }) {
  const [token, setToken] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/queue-token");
      setToken(data.token);
      setUpdatedAt(data.updated_at || "");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const publicUrl = token ? `${window.location.origin}/queue/${token}` : "";

  const copyUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // fallback
      const el = document.createElement("textarea");
      el.value = publicUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  const rotate = async () => {
    if (!window.confirm("Gerar novo token? O link atual deixará de funcionar imediatamente em todas as TVs.")) return;
    setRotating(true);
    try {
      const { data } = await api.post("/queue-token/rotate");
      setToken(data.token);
      setUpdatedAt(data.updated_at || "");
    } finally { setRotating(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white text-black max-w-2xl w-full p-6 md:p-8 border border-black" onClick={(e) => e.stopPropagation()} data-testid="share-tv-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="overline text-zinc-500 mb-1">EXIBIÇÃO PÚBLICA</div>
            <h2 className="font-head text-2xl md:text-3xl font-black tracking-tighter">Compartilhar TV da Fila</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-black" data-testid="close-share-tv">
            <X size={22} />
          </button>
        </div>

        <p className="text-sm text-zinc-600 mb-5">
          Use o link abaixo em qualquer TV ou tablet do chão de fábrica. Não precisa de login. O link funciona até você rotacionar o token.
        </p>

        {loading ? (
          <div className="text-zinc-400 text-sm">Carregando token…</div>
        ) : (
          <>
            <div className="border border-black bg-zinc-50 p-3 flex items-center gap-2 mb-3 font-mono text-sm break-all" data-testid="public-queue-url">
              {publicUrl}
            </div>

            <div className="flex flex-wrap gap-2 mb-5">
              <button className="btn flex-1 min-w-[180px]" onClick={copyUrl} data-testid="copy-public-url">
                {copied ? <><Check size={16} weight="bold" /> Copiado!</> : <><Copy size={16} weight="bold" /> Copiar Link</>}
              </button>
              <a className="btn btn-ghost flex-1 min-w-[180px]" href={publicUrl} target="_blank" rel="noopener noreferrer" data-testid="open-public-url">
                <Television size={16} weight="bold" /> Abrir em Nova Aba
              </a>
            </div>

            <div className="border-t border-zinc-200 pt-4">
              <div className="overline text-zinc-500 mb-2">SEGURANÇA</div>
              <div className="text-xs text-zinc-500 mb-3">
                Última atualização do token: <span className="font-mono">{updatedAt ? new Date(updatedAt).toLocaleString("pt-BR") : "—"}</span>
              </div>
              <button
                className="btn"
                style={{ background: "#E60000", color: "white", borderColor: "#E60000" }}
                onClick={rotate}
                disabled={rotating}
                data-testid="rotate-token-btn"
              >
                <ArrowsClockwise size={16} weight="bold" className={rotating ? "animate-spin" : ""} />
                {rotating ? "Rotacionando…" : "Rotacionar Token"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function SerigrafiaQueue() {
  const { token } = useParams();
  const [separacao, setSeparacao] = useState([]);
  const [serigrafia, setSerigrafia] = useState([]);
  const [now, setNow] = useState(new Date());
  const [showShare, setShowShare] = useState(false);
  const user = getUser();
  const { canAdmin } = useCanAdmin();
  const isPublicMode = Boolean(token);

  const apply = (data) => {
    if (Array.isArray(data)) {
      // Backwards-compat: old endpoint returned a flat array
      setSeparacao(data.filter((o) => o.production_status === "separacao"));
      setSerigrafia(data.filter((o) => o.production_status === "serigrafia"));
    } else {
      setSeparacao(data?.separacao || []);
      setSerigrafia(data?.serigrafia || []);
    }
  };

  const load = useCallback(async () => {
    try {
      if (isPublicMode) {
        const { data } = await axios.get(`${API}/public/queue/${token}`);
        apply(data);
      } else {
        const { data } = await api.get("/orders/queue/serigrafia");
        apply(data);
      }
    } catch (e) {
      console.warn("Serigrafia queue load failed:", e?.response?.status || e?.message);
    }
  }, [isPublicMode, token]);

  useEffect(() => {
    load();
    // Polling: fallback lento (WebSocket cobre o tempo real nos dois modos)
    const i = setInterval(load, isPublicMode ? 60000 : 30000);
    const t = setInterval(() => setNow(new Date()), 1000);
    let ws = null;
    let retry = null;
    let closed = false;
    if (isPublicMode) {
      const connect = () => {
        try {
          ws = new WebSocket(`${API.replace(/^http/, "ws")}/ws/queue/${token}`);
          ws.onmessage = () => load();
          ws.onclose = () => {
            if (!closed) retry = setTimeout(connect, 5000);
          };
        } catch (e) {
          console.warn("Serigrafia WS connect failed, polling fallback active:", e?.message);
        }
      };
      connect();
    }
    return () => {
      clearInterval(i);
      clearInterval(t);
      closed = true;
      if (retry) clearTimeout(retry);
      if (ws) { ws.onclose = null; ws.close(); }
    };
  }, [token, isPublicMode, load]);

  // Modo admin (logado): tempo real via canal autenticado /ws/updates
  useRealtimeOrders(() => {
    if (!isPublicMode) load();
  });

  const next = serigrafia[0];
  const rest = serigrafia.slice(1, 15);
  const totalCups = (o) => {
    const fromItems = o?.total_cups || (o?.order_items?.reduce((a, b) => a + (b.cups || 0), 0)) || 0;
    if (fromItems > 0) return fmtInt(fromItems);
    const fromProd = o?.production_items?.reduce((a, b) => a + (parseInt(b.qty) || 0), 0) || 0;
    return fmtInt(fromProd);
  };

  const fmtDeadline = (s) => {
    if (!s) return null;
    try {
      const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
      return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
    } catch { return null; }
  };

  return (
    <div className="tv flex flex-col min-h-screen lg:h-screen lg:max-h-screen lg:overflow-hidden" data-testid="serigrafia-queue">
      {/* Header — compacto para valorizar as filas */}
      <div className="flex items-center justify-between px-6 lg:px-8 py-2.5 border-b border-white/20 shrink-0">
        <div className="flex items-center gap-3">
          <Television size={22} weight="bold" />
          <div className="flex items-baseline gap-3">
            <div className="font-head text-lg lg:text-xl font-black">SERIGRAFIA</div>
            <div className="overline text-white/50 text-[9px]">FILA DE PRODUÇÃO · TEMPO REAL</div>
          </div>
        </div>
        <div className="text-right flex items-center gap-4">
          {!isPublicMode && canAdmin && (
            <button
              className="btn btn-ghost !py-1.5 !px-3 text-[10px]"
              style={{ borderColor: "rgba(255,255,255,0.4)", color: "white" }}
              onClick={() => setShowShare(true)}
              data-testid="share-tv-btn"
            >
              <ShareNetwork size={14} weight="bold" />
              <span>Compartilhar TV</span>
            </button>
          )}
          <div className="flex items-baseline gap-2">
            <div className="font-mono text-xl lg:text-2xl">{now.toLocaleTimeString("pt-BR")}</div>
            <div className="overline text-white/50 text-[9px] hidden lg:block">{now.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}</div>
          </div>
        </div>
      </div>

      {/* Body: 25% Separação | 75% Serigrafia — cada fila com scroll próprio */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-4 gap-0 lg:min-h-0 lg:overflow-hidden">
        {/* LEFT 25%: Coluna Separação */}
        <div className="lg:col-span-1 p-5 lg:p-6 border-r border-white/20 flex flex-col bg-white/[0.02] lg:min-h-0 lg:overflow-hidden" data-testid="separacao-column">
          <div className="overline text-white/60 mb-4 flex items-center gap-2 shrink-0">
            <Clock size={14} weight="bold" /> SEPARAÇÃO · {fmtInt(separacao.length)}
          </div>
          <div className="space-y-2 flex-1 min-h-0 overflow-y-auto" data-testid="separacao-scroll">
            {separacao.length === 0 && <div className="text-white/40 text-sm">Sem pedidos em separação.</div>}
            {separacao.map((o, idx) => (
              <div key={o.id} className="border border-white/20 p-3 hover:bg-white/5 transition-colors" data-testid={`separacao-item-${o.bling_number}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="font-mono text-white/40 text-sm">{idx + 1}.</span>
                      <span className="font-mono text-lg font-bold">#{o.bling_number}</span>
                    </div>
                    <div className="text-xs text-white/80 truncate font-semibold mt-1">{o.customer}</div>
                    {fmtDeadline(o.exit_date) && (
                      <div className="text-[10px] text-[#FFC000] mt-1 font-mono">prazo: {fmtDeadline(o.exit_date)}</div>
                    )}
                    <div className="mt-1.5 flex items-baseline gap-1.5">
                      <span className="font-mono text-lg font-bold text-[#FFC000]">{totalCups(o)}</span>
                      <span className="text-[9px] text-white/40 uppercase tracking-widest">copos</span>
                    </div>
                  </div>
                  <div className="shrink-0">
                    <OrderThumbs order={o} sizePx={75} max={2} dark testid={`separacao-thumbs-${o.bling_number}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT 75%: Coluna Serigrafia (hero + fila) */}
        <div className="lg:col-span-3 grid grid-cols-1 lg:grid-cols-7 gap-0 lg:min-h-0 lg:overflow-hidden">
          {/* HERO */}
          <div className="lg:col-span-4 p-6 lg:p-10 border-r border-white/20 flex flex-col lg:min-h-0 lg:overflow-y-auto" data-testid="next-order">
            {next ? (
              <>
                <div className="overline text-[#FFC000] mb-4 flex items-center gap-3">
                  <Warning size={18} weight="fill" /> PRÓXIMO A SERIGRAFAR
                </div>

                <div className="flex items-baseline justify-between border-b border-white/20 pb-5 mb-5">
                  <div>
                    <div className="overline text-white/60 text-[10px]">PEDIDO</div>
                    <div className="font-mono text-6xl lg:text-7xl font-semibold leading-none">#{next.bling_number}</div>
                  </div>
                  <div className="text-right max-w-[50%]">
                    <div className="overline text-white/60 text-[10px]">CLIENTE</div>
                    <div className="font-head text-2xl lg:text-3xl font-bold leading-tight truncate">{next.customer}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 mb-6">
                  <div>
                    <div className="overline text-white/60 mb-2 text-[10px] flex items-center gap-1"><Tag size={11} /> CATEGORIA</div>
                    <div className="font-head text-lg font-bold">{next.category || "—"}</div>
                  </div>
                  <div>
                    <div className="overline text-white/60 mb-2 text-[10px] flex items-center gap-1"><Storefront size={11} /> MARKETPLACE</div>
                    <div className="font-mono text-base">{next.marketplace || "—"}</div>
                  </div>
                  <div>
                    <div className="overline text-[#FFC000] mb-2 text-[10px]">PRAZO DE ENVIO</div>
                    <div className="font-mono text-base font-bold">{fmtDeadline(next.exit_date) || <span className="text-white/30">— sem prazo —</span>}</div>
                  </div>

                  <div>
                    <div className="overline text-[#FFC000] mb-2 text-[10px]">COR DO SILK</div>
                    <div className="font-head text-xl font-bold">{next.silk_color || <span className="text-white/30">— não definido —</span>}</div>
                  </div>
                  <div>
                    <div className="overline text-white/60 mb-2 text-[10px]">TOTAL COPOS</div>
                    <div className="font-mono text-4xl font-semibold text-[#00FF7F]">{totalCups(next)}</div>
                  </div>
                  <div>
                    <div className="overline text-white/60 mb-2 text-[10px]">SITUAÇÃO BLING</div>
                    <div className="font-mono text-sm text-white/70">{next.bling_situation || "—"}</div>
                  </div>
                </div>

                {next.order_items?.length > 0 && (
                  <div className="border-t border-white/20 pt-4 mb-4">
                    <div className="overline text-white/60 mb-2 text-[10px]">SKUs DO PEDIDO</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      {next.order_items.map((it, i) => (
                        <div key={`${it.sku}-${i}`} className="flex items-center justify-between gap-2 border border-white/15 px-3 py-2 font-mono" data-testid={`hero-sku-${i}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            {it.image_url && (
                              <img src={it.image_url} alt={`arte ${it.sku}`} className="h-10 w-10 object-contain bg-white border border-white/30 shrink-0" />
                            )}
                            <span className="truncate"><span className="font-bold">{it.sku}</span> · {it.qty}×</span>
                          </div>
                          <span className="text-[#FFC000] shrink-0">{it.cups} copos</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {next.image_url && (
                  <div className="border-t border-white/20 pt-4 flex items-center gap-3">
                    <ImageIcon size={16} className="text-white/60" />
                    <div className="overline text-white/60 text-[10px]">ARTE</div>
                    <img src={next.image_url} alt="arte" className="h-16 w-16 object-contain border border-white/20" />
                  </div>
                )}
              </>
            ) : (
              <div className="text-white/40 text-center my-auto">
                <Television size={64} weight="thin" className="mx-auto mb-4" />
                <div className="overline">FILA VAZIA</div>
                <div className="font-head text-3xl mt-2">Sem pedidos aguardando serigrafia</div>
              </div>
            )}
          </div>

          {/* Próximos da Serigrafia */}
          <div className="lg:col-span-3 p-6 lg:p-8 flex flex-col lg:min-h-0 lg:overflow-hidden">
            <div className="overline text-white/60 mb-4 flex items-center gap-2 shrink-0">
              <Clock size={14} weight="bold" /> NA FILA SERIGRAFIA · {fmtInt(serigrafia.length)}
            </div>
            <div className="space-y-2 flex-1 min-h-0 overflow-y-auto" data-testid="serigrafia-scroll">
              {rest.length === 0 && <div className="text-white/40 text-sm">Sem pedidos adicionais.</div>}
              {rest.map((o, idx) => (
                <div key={o.id} className="border border-white/20 p-3 hover:bg-white/5 transition-colors" data-testid={`queue-item-${o.bling_number}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="font-mono text-2xl text-white/40 w-8 text-right">{idx + 2}</div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-mono text-lg font-bold">#{o.bling_number}</span>
                          {fmtDeadline(o.exit_date) && (
                            <span className="text-[10px] overline px-2 py-0.5 border border-[#FFC000] text-[#FFC000]">{fmtDeadline(o.exit_date)}</span>
                          )}
                        </div>
                        <div className="text-sm text-white/80 truncate font-semibold">{o.customer}</div>
                        <div className="text-[11px] text-white/50 mt-1 flex flex-wrap gap-x-3">
                          <span>{o.marketplace}</span>
                          <span>{o.category}</span>
                          {o.silk_color && <span className="text-[#FFC000]">silk: {o.silk_color}</span>}
                        </div>
                        <div className="mt-1.5 flex items-baseline gap-1.5">
                          <span className="font-mono text-2xl font-bold text-[#FFC000]">{totalCups(o)}</span>
                          <span className="text-[9px] text-white/40 uppercase tracking-widest">copos</span>
                        </div>
                      </div>
                    </div>
                    <div className="shrink-0">
                      <OrderThumbs order={o} sizePx={75} max={2} dark testid={`queue-thumbs-${o.bling_number}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Marquee bottom */}
      <div className="border-t border-white/20 p-3 overflow-hidden shrink-0">
        <div className="overline text-white/60 mb-1 text-[10px]">EM PRÉ-PRODUÇÃO ({serigrafia.length + separacao.length})</div>
        <div className="flex gap-6 whitespace-nowrap animate-marquee">
          {serigrafia.concat(serigrafia).map((o, i) => (
            <span key={`${o.id}-${i}`} className="font-mono text-sm">
              #{o.bling_number} · {o.customer} · <span className="text-[#FFC000]">{totalCups(o)} copos</span>
            </span>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-marquee { animation: marquee 50s linear infinite; }
      `}</style>

      {showShare && <ShareTvModal onClose={() => setShowShare(false)} />}
    </div>
  );
}
